import { GradientScrollDiv } from '@components/util/GradientScrollDiv';
import { CenteredCardProvider } from '@lib/hooks/CenteredCardContext';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { userStore } from '@stores/User';
import { observer } from 'mobx-react';
import { RefObject } from 'react';
import css from './LibraryCards.module.scss';
import { LibraryDeleteModalRef } from './LibraryDeleteModal';
import { LibraryGridCard } from './LibraryGridCard';
import { LibraryRenameModalRef } from './LibraryRenameModal';
import { renderLibrarySections } from './renderLibrarySections';

interface LibraryCardsProps {
  onLibrarySelect: (id: string, name: string) => void;
  renameModalRef: RefObject<LibraryRenameModalRef | null>;
  deleteModalRef: RefObject<LibraryDeleteModalRef | null>;
  onMobileMenuClick?: (libraryId: string, libraryName: string, thumbnailBlobUrl?: string) => void;
  hiddenLibraryId?: string | null;
  /** Opens the create-library modal, which the parent owns so both views share it. */
  onNewLibrary: () => void;
  /**
   * Opens the add-media modal for one library. Owned by the parent for the same
   * reason as `onNewLibrary`: one modal for the whole grid rather than one per card.
   * Only reachable from a library with no media.
   */
  onAddMedia: (libraryId: string) => void;
}

export const LibraryCards = observer(
  ({
    onLibrarySelect,
    renameModalRef,
    deleteModalRef,
    onMobileMenuClick,
    hiddenLibraryId,
    onNewLibrary,
    onAddMedia,
  }: LibraryCardsProps) => {
    const libraryManager = useLibraryManager();
    const isMobile = useIsMobile();
    // Only verified users can create libraries (mirrors the nav "New library" button).
    const canCreate = !!userStore.data?.isVerified;

    const grid = (
      <GradientScrollDiv className={css.libraryCardsContainer}>
        {/* One grid for every section, with the headers as full-row children rather than a
            wrapper per section — so `auto-fill` column packing, the gap and card sizing are
            all exactly as they were before sections existed. */}
        <div className={css.libraryCardsContainerInner}>
          {/* Every card prop here is a primitive, a stable store reference or a stable
              callback, which is what lets `LibraryGridCard`'s memo actually skip siblings on a
              reorder. Don't inline a closure or an object literal into this list — see the
              docstring on `LibraryGridCard`. */}
          {renderLibrarySections(libraryManager.librarySections, {
            labelClassName: css.sectionLabel,
            renderItem: (library) => (
              <LibraryGridCard
                key={library.libraryId}
                library={library}
                isHidden={hiddenLibraryId === library.libraryId}
                showPin={!isMobile}
                onLibrarySelect={onLibrarySelect}
                onMobileMenuClick={onMobileMenuClick}
                onAddMedia={onAddMedia}
                renameModalRef={renameModalRef}
                deleteModalRef={deleteModalRef}
              />
            ),
          })}
          {canCreate && (
            <button
              type="button"
              className={css.newLibraryTile}
              onClick={onNewLibrary}
              aria-label="Create a new library"
            >
              <AddRoundedIcon className={css.newLibraryTileIcon} />
              <span>New library</span>
            </button>
          )}
        </div>
      </GradientScrollDiv>
    );

    // Mounted unconditionally; the provider decides for itself whether the centre line is needed
    // (only where the device can't hover) and stays inert otherwise. Same arrangement as
    // LibraryContentMediaList and MomentGrid — one rule for all three grids.
    return <CenteredCardProvider>{grid}</CenteredCardProvider>;
  },
);
